"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const NAMESPACE = "new-fdm-fused-deposition-modeling";

const IMAGE_BASE = "/image/new-version/more/FDM/Production";

const CATEGORIES = [
	{ titleKey: "fdmApplicationsCategory1Title", descKey: "fdmApplicationsCategory1Desc" },
	{ titleKey: "fdmApplicationsCategory2Title", descKey: "fdmApplicationsCategory2Desc" },
	{ titleKey: "fdmApplicationsCategory3Title", descKey: "fdmApplicationsCategory3Desc" },
] as const;

const IMAGES = [
	{
		src: `${IMAGE_BASE}/consumer-products.webp`,
		labelKey: "fdmApplicationsImageLabel1",
		href: "/industry/consumer-goods",
	},
	{
		src: `${IMAGE_BASE}/robotics.webp`,
		labelKey: "fdmApplicationsImageLabel2",
		href: "/industry/robotics-automation",
	},
	{
		src: `${IMAGE_BASE}/electronic-devices.webp`,
		labelKey: "fdmApplicationsImageLabel3",
		href: "/industry/consumer-electronics",
	},
] as const;

export default function FdmApplications() {
	const t = useTranslations(NAMESPACE);

	return (
		<section className="bg-[#ffffff] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
					<span className="mb-3 inline-block text-xl uppercase tracking-wider text-themePrimary400 max-md:text-lg">
						{t("fdmApplicationsEyebrow")}
					</span>
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("fdmApplicationsTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("fdmApplicationsDesc")}</p>
				</div>

				<div className="grid items-start gap-8 lg:grid-cols-[6fr_4fr] lg:gap-10 xl:gap-14">
					<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-2">
						{CATEGORIES.map((cat, index) => (
							<div key={cat.titleKey} className={index === 2 ? "sm:col-span-2" : ""}>
								<h3 className="mb-2 text-base font-semibold text-themePrimary400 sm:text-lg">
									{t(cat.titleKey)}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">{t(cat.descKey)}</p>
							</div>
						))}
					</div>

					<div className="grid grid-cols-2 gap-3">
						<div className="col-start-2">
							<Link
								href={IMAGES[0].href}
								className="group flex h-full flex-col rounded-xl bg-[#ededed] p-4 transition-colors duration-300 hover:bg-themePrimary400"
							>
								<div className="relative h-16 w-16 overflow-hidden rounded-full sm:h-20 sm:w-20">
									<Image
										src={IMAGES[0].src}
										alt={t(IMAGES[0].labelKey)}
										fill
										className="object-cover"
										sizes="80px"
									/>
								</div>
								<span className="mt-4 self-end text-right text-sm font-semibold text-[#1f1f1f] transition-colors duration-300 group-hover:text-white sm:text-base">
									{t(IMAGES[0].labelKey)}
								</span>
							</Link>
						</div>
						{IMAGES.slice(1).map((image) => (
							<Link
								key={image.labelKey}
								href={image.href}
								className="group flex h-full flex-col rounded-xl bg-[#ededed] p-4 transition-colors duration-300 hover:bg-themePrimary400"
							>
								<div className="relative h-16 w-16 overflow-hidden rounded-full sm:h-20 sm:w-20">
									<Image src={image.src} alt={t(image.labelKey)} fill className="object-cover" sizes="80px" />
								</div>
								<span className="mt-4 self-end text-right text-sm font-semibold text-[#1f1f1f] transition-colors duration-300 group-hover:text-white sm:text-base">
									{t(image.labelKey)}
								</span>
							</Link>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
